import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mantine/core";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SettingsFormContent, SettingsFormCTAs } from ".";
import { CameraValidationSchema, CameraFormInput } from "./validation/CameraValidationSchema";
import {
  useGetCameraWithWasteStreamByCompanyId,
  postCreateCamera,
  putUpdateCamera,
  deleteCamera,
  postLinkCameraToWasteStream,
  postDisableCameraMotionSchedule,
  postEnableCameraMotionSchedule,
} from "@/services/CameraControll";
import { useGetContractsLight } from "@/services/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { Camera, CameraSchedule, ExtendedCamera } from "@/types";
import WasteStreamTypeEnum from "@/constants/enums/WasteStreamTypeEnum";
import CameraScheduleEnum from "@/constants/enums/CameraScheduleEnum";
import Heading from "../../atoms/Heading";
import Stack from "../../atoms/Stack";
import TextInput from "../../atoms/TextInput";
import NumberInput from "../../atoms/NumberInput";
import Select from "../../atoms/Select";
import CTA from "../../atoms/CTA";
import Group from "../../atoms/Group";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import Slider from "@/components/atoms/Slider";
import ContractStatusEnum from "@/constants/enums/ContractStatusEnum";
import { notifications } from "@mantine/notifications";
import { SuccessNotificationObj, ErrorNotificationObj } from "@/utils/NotificationUtil";

const wasteStreamOptions = Object.entries(WasteStreamTypeEnum)
  .filter(([key]) => isNaN(Number(key)))
  .map(([key, value]) => ({ value: String(value), label: key }));

const scheduleModeOptions = Object.entries(CameraScheduleEnum)
  .filter(([key]) => isNaN(Number(key)))
  .map(([key]) => ({ value: key, label: key }));

export default function Cameras() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [cameraToEdit, setCameraToEdit] = useState<ExtendedCamera | null>(null);

  const { currentCompany, isAdminUser } = useAuth();
  const companyId = currentCompany?.companyId as number;
  const isAdmin = isAdminUser(currentCompany);

  const { openModal, closeModal } = useModal();

  const { data: cameras } = useGetCameraWithWasteStreamByCompanyId(companyId, { enabled: !!companyId });
  const { data: contractsData, isLoading: isLoadingContracts } = useGetContractsLight({
    limit: 1000,
    pageNumber: 0,
    showDeleted: false,
    statuses: [ContractStatusEnum.ACTIVE],
  });

  const contractOptions = useMemo(
    () => (contractsData?.contracts ?? []).map((c) => ({ value: String(c.id), label: `${c.name} (${c.id})` })),
    [contractsData]
  );

  const {
    handleSubmit,
    control,
    reset,
    formState: { dirtyFields },
  } = useForm<CameraFormInput>({
    resolver: zodResolver(CameraValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      ipAddress: "",
      location: "",
      captureIntervalSeconds: undefined,
      username: "",
      password: "",
      apiEndpointPattern: "",
      contractId: "",
      wasteStreamTypeId: "",
      scheduleMode: "",
      scheduleIntervalSeconds: undefined,
      sensitivityThreshold: 0.5,
    },
  });

  useEffect(() => {
    if (cameraToEdit) {
      reset({
        ipAddress: cameraToEdit.ipAddress,
        location: cameraToEdit.location,
        captureIntervalSeconds: cameraToEdit.captureIntervalSeconds,
        username: cameraToEdit.username,
        password: "",
        apiEndpointPattern: cameraToEdit.apiEndpointPattern,
        contractId: cameraToEdit.wasteStream ? String(cameraToEdit.wasteStream.contractId) : "",
        wasteStreamTypeId: cameraToEdit.wasteStream
          ? String(WasteStreamTypeEnum[cameraToEdit.wasteStream.wasteStreamType])
          : "",
        scheduleMode: cameraToEdit.mode ?? "",
        scheduleIntervalSeconds: cameraToEdit.captureIntervalSeconds,
        sensitivityThreshold: cameraToEdit.sensitivityThreshold ?? 0.5,
      });
    } else {
      reset({
        ipAddress: "",
        location: "",
        captureIntervalSeconds: undefined,
        username: "",
        password: "",
        apiEndpointPattern: "",
        contractId: "",
        wasteStreamTypeId: "",
        scheduleMode: "",
        scheduleIntervalSeconds: undefined,
        sensitivityThreshold: 0.5,
      });
    }
  }, [cameraToEdit, reset]);

  const { mutateAsync: mutateCreateAsync, isPending: isPendingCreate } = useMutation({
    mutationFn: (data: CameraFormInput) =>
      postCreateCamera(companyId, {
        ipAddress: data.ipAddress,
        location: data.location,
        captureIntervalSeconds: data.captureIntervalSeconds ?? 0,
        username: data.username ?? "",
        password: data.password ?? "",
        apiEndpointPattern: data.apiEndpointPattern ?? "",
      }),
  });

  const { mutateAsync: mutateUpdateAsync, isPending: isPendingUpdate } = useMutation({
    mutationFn: (data: CameraFormInput) =>
      putUpdateCamera(cameraToEdit!.id, {
        ipAddress: data.ipAddress,
        location: data.location,
        captureIntervalSeconds: data.captureIntervalSeconds ?? 0,
        username: data.username ?? "",
        password: data.password ?? "",
        apiEndpointPattern: data.apiEndpointPattern ?? "",
        companyId,
        lastActive: cameraToEdit!.lastActive,
        status: cameraToEdit!.status,
      }),
  });

  const { mutateAsync: mutateLinkToWasteStreamAsync, isPending: isPendingLink } = useMutation({
    mutationFn: ({
      cameraId,
      contractId,
      wasteStreamTypeId,
    }: {
      cameraId: number;
      contractId: number;
      wasteStreamTypeId: number;
    }) => postLinkCameraToWasteStream(cameraId, { contractId, wasteStreamTypeId, active: true }),
  });

  const { mutateAsync: mutateEnableScheduleAsync, isPending: isPendingEnableSchedule } = useMutation({
    mutationFn: ({ cameraId, data }: { cameraId: number; data: CameraFormInput }) =>
      postEnableCameraMotionSchedule(cameraId, {
        intervalSeconds: data.scheduleIntervalSeconds ?? 0,
        sensitivityThreshold: data.sensitivityThreshold ?? 0,
        companyId,
        contractId: Number(data.contractId),
        wasteStreamTypeId: Number(data.wasteStreamTypeId),
        mode: data.scheduleMode as CameraSchedule,
      }),
  });

  const { mutateAsync: mutateDisableScheduleAsync, isPending: isPendingDisableSchedule } = useMutation({
    mutationFn: (cameraId: number) => postDisableCameraMotionSchedule(cameraId),
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: (cameraId: number) => deleteCamera(cameraId, companyId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["getCameraWithWasteStreamByCompanyId", companyId] });
      closeModal();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:camera.deleteSuccess"))
      );
    },
    onError: () =>
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("notifications:camera.deleteError"))),
  });

  const isPending =
    isPendingCreate || isPendingUpdate || isPendingLink || isPendingEnableSchedule || isPendingDisableSchedule;

  const onSubmit = async (data: CameraFormInput) => {
    const isEditing = !!cameraToEdit;
    try {
      let cameraId: number;
      if (isEditing) {
        await mutateUpdateAsync(data);
        cameraId = cameraToEdit!.id;
      } else {
        const response = await mutateCreateAsync(data);
        cameraId = (response.data as Camera).id;
      }

      if (data.contractId && data.wasteStreamTypeId && (dirtyFields.contractId || dirtyFields.wasteStreamTypeId)) {
        await mutateLinkToWasteStreamAsync({
          cameraId,
          contractId: Number(data.contractId),
          wasteStreamTypeId: Number(data.wasteStreamTypeId),
        });
      }

      if (
        data.scheduleMode &&
        (dirtyFields.scheduleMode || dirtyFields.scheduleIntervalSeconds || dirtyFields.sensitivityThreshold)
      ) {
        if (data.scheduleMode === CameraScheduleEnum[CameraScheduleEnum.DISABLED]) {
          await mutateDisableScheduleAsync(cameraId);
        } else {
          await mutateEnableScheduleAsync({ cameraId, data });
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["getCameraWithWasteStreamByCompanyId", companyId] });

      if (isEditing) {
        setCameraToEdit(null);
      } else {
        reset();
      }

      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t(isEditing ? "notifications:camera.updateSuccess" : "notifications:camera.createSuccess")
        )
      );
    } catch {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t(isEditing ? "notifications:camera.updateError" : "notifications:camera.createError")
        )
      );
    }
  };

  const columns: ColumnDef<ExtendedCamera>[] = useMemo(
    () => [
      { header: t("settings:cameras.ipAddress"), accessorKey: "ipAddress" },
      { header: t("settings:cameras.location"), accessorKey: "location" },
      {
        header: t("settings:cameras.lastActive"),
        accessorKey: "lastActive",
        cell: ({ getValue }) => {
          const val = getValue<Date>();
          return val ? new Date(val).toLocaleDateString() : "-";
        },
      },
      {
        id: "contract",
        header: t("settings:cameras.contract"),
        accessorFn: (row) => row.wasteStream?.contractId ?? null,
        cell: ({ getValue }) => {
          const contractId = getValue<number | null>();
          if (!contractId) return "-";
          const contract = contractsData?.contracts.find((c) => c.id === contractId);
          return contract ? `${contract.name} (${contractId})` : `(${contractId})`;
        },
      },
      {
        id: "wasteStreamType",
        header: t("settings:cameras.wasteStream"),
        accessorFn: (row) => row.wasteStream?.wasteStreamType ?? null,
        cell: ({ getValue }) => getValue<string | null>() ?? "-",
      },
    ],
    [t, contractsData]
  );

  const editActions = (camera: ExtendedCamera) => [
    {
      label: t("settings:edit"),
      action: () => setCameraToEdit(camera),
    },
    {
      label: t("settings:delete"),
      action: () =>
        openModal({
          isOpen: true,
          title: t("actions:confirm"),
          content: t("settings:cameras.deleteConfirmation", { location: camera.location }),
          primaryAction: {
            onClick: () => mutateDelete(camera.id),
            text: t("settings:delete"),
            intent: "secondary",
          },
          secondaryAction: {
            onClick: () => closeModal(),
            text: t("actions:cancel"),
            intent: "primary",
          },
        }),
    },
  ];

  return (
    <Stack>
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:cameras.title")}
      </Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <SettingsFormContent>
          <Grid>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="ipAddress"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id="ipAddress"
                    required
                    label={t("settings:cameras.ipAddress")}
                    value={field.value || ""}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="location"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id="location"
                    required
                    label={t("settings:cameras.location")}
                    value={field.value || ""}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="username"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id="username"
                    label={t("settings:cameras.username")}
                    value={field.value || ""}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="password"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id="password"
                    label={t("settings:cameras.password")}
                    value={field.value || ""}
                    disabled={!!cameraToEdit}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Controller
                control={control}
                name="apiEndpointPattern"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id="apiEndpointPattern"
                    label={t("settings:cameras.apiEndpointPattern")}
                    value={field.value || ""}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="contractId"
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    id="contractId"
                    label={t("settings:cameras.contract")}
                    loading={isLoadingContracts}
                    data={contractOptions}
                    value={field.value || null}
                    onChange={(val) => field.onChange(val ?? "")}
                    clearable
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="wasteStreamTypeId"
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    id="wasteStreamTypeId"
                    label={t("settings:cameras.wasteStream")}
                    data={wasteStreamOptions}
                    value={field.value || null}
                    onChange={(val) => field.onChange(val ?? "")}
                    clearable
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="scheduleMode"
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    id="scheduleMode"
                    label={t("settings:cameras.scheduleMode")}
                    data={scheduleModeOptions}
                    value={field.value || null}
                    onChange={(val) => field.onChange(val ?? "")}
                    clearable
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="scheduleIntervalSeconds"
                render={({ field, fieldState }) => (
                  <NumberInput
                    {...field}
                    id="scheduleIntervalSeconds"
                    label={t("settings:cameras.scheduleIntervalSeconds")}
                    value={field.value ?? ""}
                    onChange={(val) => field.onChange(val === "" ? undefined : val)}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={6} mb={20}>
              <Controller
                control={control}
                name="sensitivityThreshold"
                render={({ field }) => (
                  <Slider
                    {...field}
                    id="sensitivityThreshold"
                    label={t("settings:cameras.sensitivity")}
                    min={0}
                    max={1}
                    step={0.01}
                    marks={[
                      { value: 0.2, label: "20%" },
                      { value: 0.5, label: "50%" },
                      { value: 0.8, label: "80%" },
                    ]}
                    value={field.value ?? 0.5}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid.Col>
          </Grid>
        </SettingsFormContent>
        <SettingsFormCTAs>
          <Group justify="flex-end">
            {cameraToEdit && (
              <CTA intent="secondary" type="button" onClick={() => setCameraToEdit(null)}>
                {t("actions:cancel")}
              </CTA>
            )}
            <CTA intent="primary" type="submit" loading={isPending} disabled={isPending}>
              {cameraToEdit ? t("actions:update") : t("actions:save")}
            </CTA>
          </Group>
        </SettingsFormCTAs>
      </form>

      <SettingsFormContent>
        <SettingsTable data={cameras ?? []} columns={columns} editActions={editActions} isAdmin={isAdmin} />
      </SettingsFormContent>
    </Stack>
  );
}
